import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Autocomplete,
  CircularProgress, Divider,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useGetDocRepMutation, useSubmitRxMutation } from "state/api";
import CameraUpload from "components/CameraUpload";
import { toast } from "react-toastify";
import foliregLogo from "../../assets/Folireg.png";

const FoliregLogo = () => (
  <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
    <Box
      component="img"
      src={foliregLogo}
      alt="Folireg OPD Camps"
      sx={{ maxHeight: 110, width: "auto", objectFit: "contain" }}
    />
  </Box>
);

const RxTracker = () => {
  const { user } = useSelector((s) => s.auth);
  const [getDocRep] = useGetDocRepMutation();
  const [submitRx, { isLoading }] = useSubmitRxMutation();

  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split("T")[0]);
  const [newRxCount, setNewRxCount] = useState("");
  const [samplesGiven, setSamplesGiven] = useState("");
  const [samplesUnits, setSamplesUnits] = useState("");
  const [pobStrips, setPobStrips] = useState("");
  const [rxPhoto, setRxPhoto] = useState(null);
  const [pobInvoice, setPobInvoice] = useState(null);

  useEffect(() => {
    if (user?.role) {
      getDocRep({ MgCd: user.role, HqMstId: user.HqMstId })
        .unwrap()
        .then((res) => setDoctors(res.data || []))
        .catch(console.error);
    }
  }, [user, getDocRep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctor) { toast.error("Please select a doctor"); return; }
    if (!newRxCount) { toast.error("No. of new Rx generated is required"); return; }
    if (!samplesGiven) { toast.error("No. of patients Folireg sample given is required"); return; }
    if (!samplesUnits) { toast.error("Total units of Folireg samples given is required"); return; }
    if (!rxPhoto) { toast.error("Rx photo upload is required"); return; }

    const fd = new FormData();
    fd.append("EmpNo", user.id);
    fd.append("MgCd", user.role);
    fd.append("doctorName", doctor.DoctName);
    fd.append("doctCd", doctor.DoctCd || "");
    fd.append("docCategory", doctor.DocCategory || "");
    fd.append("visitDate", visitDate);
    fd.append("newRxCount", newRxCount);
    fd.append("samplesGiven", samplesGiven);
    fd.append("samplesUnits", samplesUnits);
    fd.append("pobStrips", pobStrips || "0");
    fd.append("rxPhoto", rxPhoto);
    if (pobInvoice) fd.append("pobInvoice", pobInvoice);

    try {
      await submitRx(fd).unwrap();
      toast.success("Rx tracker submitted!");
      setDoctor(null); setNewRxCount(""); setSamplesGiven("");
      setSamplesUnits(""); setPobStrips(""); setRxPhoto(null); setPobInvoice(null);
    } catch (err) {
      toast.error(err?.data?.message || "Submit failed");
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      <FoliregLogo />

      <form onSubmit={handleSubmit}>
        {/* *Dr. Name */}
        <Autocomplete
          options={doctors}
          getOptionLabel={(opt) =>
            `${opt.DoctName} (${opt.DoctCd || "N/A"}) - ${opt.DocCategory || ""}`
          }
          value={doctor}
          onChange={(_, v) => setDoctor(v)}
          renderInput={(params) => (
            <TextField {...params} label="*Dr. Name" sx={{ mb: 2 }} required />
          )}
          isOptionEqualToValue={(opt, val) => opt.DoctCd === val.DoctCd}
        />

        {/* *Date */}
        <TextField
          fullWidth type="date" label="*Date"
          value={visitDate} onChange={(e) => setVisitDate(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} required
        />

        {/* *No. of new Rx generated */}
        <TextField
          fullWidth type="number" label="*No. of new Rx generated"
          value={newRxCount} onChange={(e) => setNewRxCount(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />

        {/* *No. of patients Folireg sample given */}
        <TextField
          fullWidth type="number" label="*No. of patients Folireg sample given"
          value={samplesGiven} onChange={(e) => setSamplesGiven(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />

        {/* *Total units of Folireg samples given */}
        <TextField
          fullWidth type="number" label="*Total units of Folireg samples given"
          value={samplesUnits} onChange={(e) => setSamplesUnits(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />

        {/* *Rx photo upload (mandatory) */}
        <CameraUpload label="*Rx photo upload" onFileSelect={setRxPhoto} required />

        <Divider sx={{ my: 1 }} />

        {/* POB (optional) */}
        <TextField
          fullWidth type="number" label="POB (in strips), if any"
          value={pobStrips} onChange={(e) => setPobStrips(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }}
        />

        {/* Upload POB invoice (optional) */}
        <CameraUpload label="Upload POB invoice" onFileSelect={setPobInvoice} accept="image/*,.pdf" />

        <Button
          fullWidth variant="contained" type="submit" size="large"
          disabled={isLoading}
          sx={{ mt: 2, py: 1.5, bgcolor: "#7B0000", "&:hover": { bgcolor: "#5a0000" } }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </form>

      <Typography
        variant="body2"
        sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
      >
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>Note: </Box>
        Sections marked with{" "}
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>*</Box>
        {" "}are mandatory for the submission
      </Typography>
    </Box>
  );
};

export default RxTracker;
